import { EventBookingStatus } from '@hqo/service-booking-types';

/**
 * Booking statuses that should suppress the BOOK / CHECKOUT primary CTA
 * — anything where the user already has a booking in flight or settled.
 *
 * Includes the three "settling" statuses (PENDING / PENDING_PAYMENT /
 * WAITING_FOR_CONFIRMATION) so that a paid-event user mid-checkout
 * can't kick off a second booking before the first resolves, which
 * would risk a double-charge depending on SBS-side uniqueness.
 */
export const ACTIVE_BOOKING_STATUSES: ReadonlySet<EventBookingStatus> = new Set([
  EventBookingStatus.CONFIRMED,
  EventBookingStatus.WAITLIST,
  EventBookingStatus.CHECKED_IN,
  EventBookingStatus.PENDING,
  EventBookingStatus.PENDING_PAYMENT,
  EventBookingStatus.WAITING_FOR_CONFIRMATION,
]);

/**
 * Booking statuses that count toward the event's per-user session cap
 * (`maxSessionsPerUser`). Mirrors service-booking-service's create-time guard
 * (`booking.repository` `BOOKING_AGGREGATES_SQL` → `userDistinctSessions`),
 * which counts the active set minus WAITLIST/CANCELLED — i.e. CONFIRMED,
 * CHECKED_IN, PENDING, PENDING_PAYMENT, WAITING_FOR_CONFIRMATION.
 *
 * Critically this includes the in-flight rows: a paid booking holds its seat as
 * PENDING_PAYMENT until the charge settles to CONFIRMED, and SBS counts that
 * toward the cap. Counting only CONFIRMED/CHECKED_IN here made the swap modal
 * never open for paid events — the FE read under the limit while SBS was over
 * it (SMB-875). Derived from `ACTIVE_BOOKING_STATUSES` (the SBS list is likewise
 * built from its active set) so the two can't drift.
 */
export const CAP_COUNTED_STATUSES: ReadonlySet<EventBookingStatus> = new Set(
  [...ACTIVE_BOOKING_STATUSES].filter((status) => status !== EventBookingStatus.WAITLIST),
);
